import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { themeOrange } from "../../../../utils/colorTheme";
import done_logo from "../../../../assets/logo.webp";
import stripe_logo from "../../../../assets/stripe_logo.webp";
import { ImPlus } from "react-icons/im";
import { useDispatch, useSelector } from "react-redux";
import { vendorStripeStartedAsync } from "../../../../services/authentication";
import { asyncStatus } from "../../../../utils/asyncStatus";
import { setVendorStripeStatus } from "../../../../store/slices/user_auth_slice";
import { getVendorStatusAsync } from "../../../../services/SubscriptionService";
import { setGetVendorStausSatus } from "../../../../store/slices/subscription_slice";
import CustomModal from "../../../../component/common/CustomModal/CustomModal";
import { RxCross2 } from "react-icons/rx";
import { IoInformationCircleSharp } from "react-icons/io5";
import { MdOpenInNew, MdRefresh, MdPayment } from "react-icons/md";

const VendorAccountTab = () => {
  const dispatch = useDispatch();
  const [isOpenRegenerateLinkModal, setIsOpenRegenerateLinkModal] = useState(false);

  const { vendor_stripe_started_status, user } = useSelector((state) => state.userAuth);
  const { get_vendor_status_status, get_vendor_status_data } = useSelector(
    (state) => state.subscriptionSlice
  );

  const isCheckStripeStatusLoading = get_vendor_status_status === asyncStatus.LOADING;
  const stripeDetails = user?.stripe_details && JSON.parse(user?.stripe_details);
  const isStartedLoading = vendor_stripe_started_status === asyncStatus.LOADING;

  const _handleGetStarted = () => {
    if (user?.account_link) {
      setIsOpenRegenerateLinkModal(true);
    } else {
      dispatch(vendorStripeStartedAsync());
    }
  };

  const _handleOpenClick = () => {
    dispatch(getVendorStatusAsync());
  };

  const stripeDetailArr = [
    { label: "Type", value: stripeDetails?.type || "" },
    { label: "Business Type", value: stripeDetails?.business_type || "" },
    { label: "Currency", value: stripeDetails?.default_currency || "" },
    { label: "Transfers", value: stripeDetails?.capabilities?.transfers || "" },
    { label: "Account Status", value: user?.stripe_account_status || "" },
    { label: "Stripe Account ID", value: user?.stripe_id || "" },
  ];

  useEffect(() => {
    if (vendor_stripe_started_status === asyncStatus.SUCCEEDED && user?.account_link) {
      setIsOpenRegenerateLinkModal(false);
      window.open(user?.account_link, "_blank");
      dispatch(setVendorStripeStatus());
    }
  }, [vendor_stripe_started_status, user?.account_link]);

  useEffect(() => {
    if (get_vendor_status_status === asyncStatus.SUCCEEDED) {
      dispatch(setGetVendorStausSatus());
    }
  }, [get_vendor_status_status]);

  const isBusinessProfileMissing =
    get_vendor_status_data?.account_details?.requirements?.past_due?.some(
      (req) => req.startsWith("business_profile")
    );
  const isIndividualMissing =
    get_vendor_status_data?.account_details?.requirements?.past_due?.some(
      (req) => req.startsWith("individual")
    );

  return (
    <Stack spacing={3}>
      <hr color="#D1D1D1" style={{ margin: 0 }} />

      {user?.account_link ? (
        /* ── HAS ACCOUNT LINK: show Stripe details ── */
        <Stack spacing={3}>
          <Stack>
            <Typography
              sx={{ fontSize: "22px", fontWeight: "500", color: "#333", fontFamily: "Raleway" }}
            >
              Stripe Account{" "}
              <Typography
                component="span"
                sx={{ fontSize: "22px", fontWeight: "700", color: "#333", fontFamily: "Raleway" }}
              >
                Details
              </Typography>
            </Typography>
          </Stack>

          {/* Detail cards */}
          <Grid container spacing={2}>
            {stripeDetailArr.map(({ label, value }, i) => (
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12} key={i}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    border: "1.5px solid #ebebeb",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#aaa",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "15px", fontWeight: "600", color: "#1a1a1a", wordBreak: "break-all" }}
                  >
                    {value || (
                      <span style={{ color: "#ccc", fontStyle: "italic", fontWeight: 400 }}>
                        Not set
                      </span>
                    )}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Account link notice */}
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1.5px solid ${themeOrange}40`,
              backgroundColor: `${themeOrange}08`,
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <IoInformationCircleSharp
              style={{ color: themeOrange, fontSize: "18px", flexShrink: 0, marginTop: "2px" }}
            />
            <Typography sx={{ fontSize: "13px", color: "#555", lineHeight: 1.6 }}>
              If you're not being redirected to Stripe automatically, please{" "}
              <Link
                href={user?.account_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: themeOrange,
                  fontWeight: "700",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                click here
              </Link>{" "}
              to open the setup link directly.
            </Typography>
          </Box>
        </Stack>
      ) : (
        /* ── NO ACCOUNT LINK: onboarding card ── */
        <Box
          sx={{
            borderRadius: "16px",
            border: "1.5px solid #ebebeb",
            backgroundColor: "#fafafa",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          {/* Purple stripe matching Stripe's brand */}
          <Box
            sx={{
              px: 3,
              py: 1,
              background: "linear-gradient(90deg, #635BFF 0%, #7A73FF 100%)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MdPayment size={14} color="white" />
            <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
              PAYMENT SETUP
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{ p: 3 }}>
            {/* Logos */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={3}
              sx={{ py: 1.5 }}
            >
              <img src={done_logo} alt="Done" style={{ height: 44, objectFit: "contain" }} />
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImPlus size={12} color="#999" />
              </Box>
              <img src={stripe_logo} alt="Stripe" style={{ height: 44, objectFit: "contain" }} />
            </Stack>

            {/* Description */}
            <Typography
              sx={{
                fontSize: "14px",
                color: "#555",
                lineHeight: 1.7,
                textAlign: "center",
                maxWidth: "480px",
                mx: "auto",
              }}
            >
              To receive payments directly from customers on{" "}
              <strong style={{ color: "#1a1a1a" }}>Done Right Away</strong>, connect your Stripe
              account. This allows us to securely process payments and transfer funds directly to
              your account.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Expired link note */}
      <Box
        sx={{
          p: 2,
          borderRadius: "12px",
          border: "1.5px solid #fff3e0",
          backgroundColor: "#fffbf5",
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <IoInformationCircleSharp
          style={{ color: "#e65100", fontSize: "18px", flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.5 }}>
          <strong>Note:</strong> If you're redirected back, the link has expired. Please regenerate
          the link to continue your Stripe setup.
        </Typography>
      </Box>

      {/* Rejected account status */}
      {user?.stripe_account_status === "Rejected" && (
        <Box
          sx={{
            p: 2.5,
            borderRadius: "12px",
            border: "1.5px solid #ef9a9a",
            backgroundColor: "#ffeaea",
          }}
        >
          <Stack spacing={1}>
            {get_vendor_status_data?.message && (
              <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#c62828" }}>
                Account Requires Attention
              </Typography>
            )}
            {get_vendor_status_data?.account_details?.requirements?.past_due?.length > 0 && (
              <Typography sx={{ fontSize: "13px", color: "#555", lineHeight: 1.6 }}>
                Your Stripe profile is incomplete. Please update the{" "}
                {isBusinessProfileMissing ? "business profile" : ""}{" "}
                {isBusinessProfileMissing && isIndividualMissing ? "and " : ""}
                {isIndividualMissing ? "individual" : ""} details to complete your profile.
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* Action buttons */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} justifyContent="center">
        {user?.stripe_id && (
          <Button
            variant="contained"
            disabled={isCheckStripeStatusLoading}
            onClick={_handleOpenClick}
            startIcon={isCheckStripeStatusLoading ? null : <MdOpenInNew size={16} />}
            sx={{
              background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: "700",
              fontSize: "14px",
              px: 3,
              py: 1.25,
              boxShadow: "0 4px 12px rgba(241,90,36,0.25)",
              "&:hover": {
                background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                boxShadow: "0 4px 14px rgba(241,90,36,0.35)",
              },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {isCheckStripeStatusLoading ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              "Check Stripe Status"
            )}
          </Button>
        )}
        {!user?.stripe_account_status !== "Approved" && (
          <Button
            variant="contained"
            disabled={isStartedLoading}
            onClick={_handleGetStarted}
            startIcon={isStartedLoading ? null : <MdRefresh size={16} />}
            sx={{
              background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: "700",
              fontSize: "14px",
              px: 3,
              py: 1.25,
              boxShadow: "0 4px 12px rgba(241,90,36,0.25)",
              "&:hover": {
                background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                boxShadow: "0 4px 14px rgba(241,90,36,0.35)",
              },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {isStartedLoading && !user?.account_link ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : user?.account_link ? (
              "Re-generate Link"
            ) : (
              "Get Started"
            )}
          </Button>
        )}
      </Stack>

      {/* Regenerate Link Confirmation Modal */}
      <CustomModal
        isOpen={isOpenRegenerateLinkModal}
        setIsOpen={() => setIsOpenRegenerateLinkModal(false)}
      >
        <IconButton
          sx={{ position: "absolute", top: "12px", right: "12px" }}
          onClick={() => setIsOpenRegenerateLinkModal(false)}
        >
          <RxCross2 style={{ fontSize: "18px", color: "#666" }} />
        </IconButton>

        <Stack
          spacing={3}
          sx={{
            backgroundColor: "white",
            p: { xs: 3, sm: 4 },
            borderRadius: "16px",
            width: { xl: "30vw", lg: "35vw", md: "50vw", sm: "70vw", xs: "85vw" },
          }}
        >
          {/* Modal header */}
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a" }}>
              Regenerate Stripe Link?
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#888", lineHeight: 1.6 }}>
              Regenerating the link will replace your current account setup, and all previous data
              associated with your old link will be overwritten. Proceed only if you're sure you
              want to generate a new link.
            </Typography>
          </Stack>

          {/* Actions */}
          <Stack direction="row" gap={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => dispatch(vendorStripeStartedAsync())}
              sx={{
                background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: "700",
                fontSize: "14px",
                py: 1.25,
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(180deg, #e04d1a 0%, #b3370d 100%)",
                  boxShadow: "none",
                },
              }}
            >
              {isStartedLoading ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Yes, Regenerate"
              )}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setIsOpenRegenerateLinkModal(false)}
              sx={{
                borderColor: "#e0e0e0",
                color: "#555",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: "600",
                fontSize: "14px",
                py: 1.25,
                "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#ccc" },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </CustomModal>
    </Stack>
  );
};

export default VendorAccountTab;
